import React, { useState } from "react";
import { Star } from "lucide-react";
import { ebayBuyerInitials } from "../../lib/ebayReviews";

const COLLAPSE_AT = 160;

/**
 * Reusable marketplace / manual feedback card.
 * @param {{
 *   item: import('../../lib/reviewsTrust').MarketplaceFeedbackItem,
 *   compact?: boolean,
 *   tone?: 'light'|'dark',
 *   expandable?: boolean,
 * }} props
 */
export default function ReviewCard({
  item,
  compact = false,
  tone = "light",
  expandable = false,
}) {
  const {
    customerLabel,
    productName,
    rating,
    comment,
    sourceLabel,
    dateLabel,
    verifiedLabel,
    tags,
  } = item;

  const showRating = typeof rating === "number" && rating >= 1 && rating <= 5;
  const dark = tone === "dark";
  const long = expandable && (comment || "").length > COLLAPSE_AT;
  const [open, setOpen] = useState(false);
  const displayComment =
    long && !open ? `${comment.slice(0, COLLAPSE_AT).trim()}…` : comment;
  const initials = ebayBuyerInitials(customerLabel);

  return (
    <article
      className={`flex flex-col h-full rounded-2xl ${
        dark
          ? "border border-white/12 bg-white/[0.06] backdrop-blur-md"
          : "border border-gold/25 bg-[#FCFAF4] shadow-soft"
      } ${compact ? "p-4 md:p-5" : "p-5 md:p-6"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex self-start rounded-full px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase ${
            dark
              ? "border border-gold/35 bg-gold/10 text-gold"
              : "border border-cream bg-white text-botanical/70"
          }`}
        >
          {sourceLabel}
        </span>
        {showRating ? (
          <div
            className="flex items-center gap-0.5 shrink-0"
            aria-label={`${rating} out of 5 stars`}
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-3 w-3 ${
                  i < rating
                    ? dark
                      ? "fill-gold text-gold"
                      : "fill-gold text-gold"
                    : dark
                      ? "text-white/20"
                      : "text-botanical/25"
                }`}
                strokeWidth={1.5}
                aria-hidden
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className={`${compact ? "mt-3" : "mt-4"} flex items-start gap-3 min-w-0`}>
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold tracking-wide ${
            dark
              ? "border-white/15 bg-void/50 text-ivory"
              : "border-cream bg-white text-forest"
          }`}
          aria-hidden
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium m-0 ${dark ? "text-ivory" : "text-forest"}`}>
            {customerLabel}
          </p>
          <p className={`text-xs mt-1 m-0 line-clamp-2 ${dark ? "text-sage" : "text-botanical/65"}`}>
            {productName}
          </p>
        </div>
      </div>

      <blockquote
        className={`mt-4 leading-relaxed flex-1 m-0 font-sans ${
          dark ? "text-ivory/90" : "text-botanical/85 text-forest/90"
        } ${compact ? "text-sm" : "text-sm md:text-[0.95rem]"}`}
      >
        “{displayComment}”
      </blockquote>

      {long ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`mt-2 self-start text-[11px] underline underline-offset-2 ${
            dark
              ? "text-sage/80 hover:text-gold"
              : "text-botanical/60 hover:text-forest"
          }`}
        >
          {open ? "Show less" : "Read more"}
        </button>
      ) : null}

      {tags && tags.length > 0 && !compact ? (
        <ul className="mt-4 flex flex-wrap gap-1.5 list-none p-0 m-0">
          {tags.map((tag) => (
            <li
              key={tag}
              className={`rounded-full px-2 py-0.5 text-[10px] ${
                dark
                  ? "bg-white/[0.06] border border-white/10 text-sage"
                  : "bg-cream/80 border border-cream text-botanical/65"
              }`}
            >
              {tag}
            </li>
          ))}
        </ul>
      ) : null}

      <footer
        className={`${compact ? "mt-3" : "mt-4"} pt-3 space-y-1 ${
          dark ? "border-t border-white/10" : "border-t border-cream/90"
        }`}
      >
        {verifiedLabel ? (
          <p className="text-[10px] text-gold/90 tracking-wide m-0">{verifiedLabel}</p>
        ) : null}
        {dateLabel ? (
          <p className={`text-[11px] m-0 ${dark ? "text-sage/70" : "text-botanical/50"}`}>
            {dateLabel}
          </p>
        ) : null}
      </footer>
    </article>
  );
}
